/*
 * Product page: gallery (swipe, thumbnails, dots, zoom lightbox) and variant picker.
 * Loaded only by sections/main-product.liquid.
 */

// Description: "read more" swaps the 3-line preview for the full text
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-desc-toggle]');
  if (!toggle) return;
  const box = toggle.closest('[data-desc]');
  const open = !box.classList.contains('is-open');
  box.classList.toggle('is-open', open);
  box.querySelector('.product__desc-full').hidden = !open;
  toggle.setAttribute('aria-expanded', String(open));
});

// Buttons keep their label in a span so the hover roll keeps working
const setLabel = (button, text) => {
  const label = button.querySelector('.button__label') || button;
  label.textContent = text;
};

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.track.children];
    this.thumbs = [...this.querySelectorAll('.gallery__thumb')];
    this.dots = [...this.querySelectorAll('.gallery__dot')];
    this.lightbox = this.querySelector('[data-gallery-lightbox]');

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index))));
    this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => this.openLightbox());
    this.track.addEventListener('click', (event) => {
      if (event.target.closest('.gallery__image')) this.openLightbox();
    });

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(this.slides.indexOf(entry.target));
      });
    }, { root: this.track, threshold: [0.6] });
    this.slides.forEach((slide) => this.observer.observe(slide));

    const start = Number(this.dataset.start || 0);
    if (start > 0) this.goTo(start, 'instant');
    else this.setActive(0);

    this.lightbox?.addEventListener('click', (event) => {
      const slide = event.target.closest('.gallery__lightbox-slide');
      if (slide && event.target.tagName === 'IMG') slide.classList.toggle('is-zoomed');
    });
  }

  goTo(index, behavior = 'smooth') {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior });
    this.setActive(index);
  }

  goToMedia(mediaId) {
    const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (index >= 0) this.goTo(index);
  }

  setActive(index) {
    this.index = index;
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
  }

  openLightbox() {
    if (!this.lightbox) return;
    this.lightbox.showModal();
    const target = this.lightbox.querySelectorAll('.gallery__lightbox-slide')[this.index || 0];
    target?.scrollIntoView({ behavior: 'instant', inline: 'start' });
  }
}
customElements.define('product-gallery', ProductGallery);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.page = this.closest('product-page');
    this.addEventListener('change', () => this.onChange());
  }

  selectedOptions() {
    return [...this.querySelectorAll('fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
  }

  async onChange() {
    const options = this.selectedOptions();
    this.querySelectorAll('fieldset').forEach((fieldset, i) => {
      const label = fieldset.querySelector('[data-option-value]');
      if (label) label.textContent = options[i];
    });

    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    const form = document.getElementById(this.dataset.formId);
    const addButton = form?.querySelector('[data-add-button]');

    if (!variant) {
      if (addButton) {
        addButton.disabled = true;
        setLabel(addButton, window.Cosmo?.strings?.unavailable || 'unavailable');
      }
      return;
    }

    form.querySelector('[data-variant-id]').value = variant.id;
    if (addButton) {
      addButton.disabled = !variant.available;
      setLabel(addButton, variant.available ? window.Cosmo?.strings?.addToCart : window.Cosmo?.strings?.soldOut);
    }

    const url = new URL(this.page.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (variant.featured_media) this.page.querySelector('product-gallery')?.goToMedia(variant.featured_media.id);

    // Refresh the price row (and anything else variant-dependent) from the server
    try {
      url.searchParams.set('section_id', this.page.dataset.sectionId);
      const html = await fetch(url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshPrice = doc.querySelector('[data-product-price]');
      const price = this.page.querySelector('[data-product-price]');
      if (freshPrice && price) price.replaceChildren(...freshPrice.childNodes);
      doc.querySelectorAll('.variant-picker__pill').forEach((pill, i) => {
        const current = this.querySelectorAll('.variant-picker__pill')[i];
        current?.classList.toggle('is-unavailable', pill.classList.contains('is-unavailable'));
      });
    } catch (error) {
      /* the price stays as it was; adding to cart still uses the right variant */
    }
  }
}
customElements.define('variant-picker', VariantPicker);

customElements.define('product-page', class extends HTMLElement {});
